import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { API, formatDate } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Plus, Search, User, Phone, Mail, MapPin, Edit2, Trash2, FileText, Building } from 'lucide-react';

// Validation simple format email
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ClientsPage = () => {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    address: '',
    city: '',
    postal_code: '',
    phone: '',
    email: '',
    notes: '',
    client_type: 'particulier',
    siren: ''
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des clients');
    }
    setLoading(false);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      company: '',
      address: '',
      city: '',
      postal_code: '',
      phone: '',
      email: '',
      notes: '',
      client_type: 'particulier',
      siren: ''
    });
    setEditingClient(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.address || !formData.city || !formData.postal_code) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }
    if (!formData.email) {
      toast.error('L\'email est obligatoire');
      return;
    }
    if (!EMAIL_REGEX.test(formData.email)) {
      toast.error('Format d\'email invalide');
      return;
    }

    try {
      if (editingClient) {
        await axios.put(`${API}/clients/${editingClient.id}`, formData);
        toast.success('Client modifié');
      } else {
        await axios.post(`${API}/clients`, formData);
        toast.success('Client ajouté');
      }
      fetchClients();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      company: client.company || '',
      address: client.address,
      city: client.city,
      postal_code: client.postal_code,
      phone: client.phone || '',
      email: client.email || '',
      notes: client.notes || '',
      client_type: client.client_type || 'particulier',
      siren: client.siren || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (clientId) => {
    try {
      await axios.delete(`${API}/clients/${clientId}`);
      toast.success('Client supprimé');
      fetchClients();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleOpenDialog = () => {
    if (canWrite()) setDialogOpen(true);
    else navigate('/subscription');
  };

  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (client.company && client.company.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="page-container p-4">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/3"></div>
          <div className="h-12 bg-muted rounded"></div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-24 bg-muted rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container p-4 md:p-8 noise-overlay">
      <div className="relative z-10 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight uppercase">Clients</h1>
          <Button
            onClick={handleOpenDialog}
            className="h-12 px-6 bg-primary text-primary-foreground hover:bg-accent font-bold uppercase tracking-wide rounded-full"
            data-testid="add-client-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            <span className="hidden sm:inline">Ajouter</span>
          </Button>
        </div>

        {/* Dialog (controlled, no DialogTrigger) */}
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogContent
            onPointerDownOutside={(e) => e.preventDefault()}
            onInteractOutside={(e) => e.preventDefault()}
            className="bg-card border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold uppercase">
                {editingClient ? 'Modifier le client' : 'Nouveau client'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nom *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="h-12 bg-muted/50 border-transparent focus:border-primary"
                  placeholder="Jean Dupont"
                  data-testid="client-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company">Société</Label>
                <Input
                  id="company"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="h-12 bg-muted/50 border-transparent focus:border-primary"
                  placeholder="Entreprise SAS"
                  data-testid="client-company-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">Adresse *</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="h-12 bg-muted/50 border-transparent focus:border-primary"
                  placeholder="123 Rue de la Paix"
                  data-testid="client-address-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="postal_code">Code postal *</Label>
                  <Input
                    id="postal_code"
                    value={formData.postal_code}
                    onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                    className="h-12 bg-muted/50 border-transparent focus:border-primary"
                    placeholder="75001"
                    data-testid="client-postal-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city">Ville *</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="h-12 bg-muted/50 border-transparent focus:border-primary"
                    placeholder="Paris"
                    data-testid="client-city-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Téléphone</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="h-12 bg-muted/50 border-transparent focus:border-primary"
                    placeholder="06 12 34 56 78"
                    data-testid="client-phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="h-12 bg-muted/50 border-transparent focus:border-primary"
                    placeholder="client@email.fr"
                    data-testid="client-email-input"
                  />
                </div>
              </div>
              {/* Client type and SIREN */}
              <div className="space-y-2">
                <Label htmlFor="client_type">Type de client</Label>
                <Select
                  value={formData.client_type}
                  onValueChange={(v) => setFormData({ ...formData, client_type: v })}
                >
                  <SelectTrigger className="h-12 bg-muted/50 border-transparent" data-testid="client-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-white/10">
                    <SelectItem value="particulier">Particulier</SelectItem>
                    <SelectItem value="professionnel">Professionnel</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {formData.client_type === 'professionnel' && (
                <div className="space-y-2">
                  <Label htmlFor="siren">SIREN</Label>
                  <Input
                    id="siren"
                    value={formData.siren}
                    onChange={(e) => setFormData({ ...formData, siren: e.target.value })}
                    className="h-12 bg-muted/50 border-transparent focus:border-primary"
                    placeholder="9 chiffres"
                    maxLength={9}
                    data-testid="client-siren-input"
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="bg-muted/50 border-transparent focus:border-primary min-h-[80px]"
                  placeholder="Notes additionnelles..."
                  data-testid="client-notes-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full h-12 bg-primary text-primary-foreground hover:bg-accent font-bold uppercase tracking-wide rounded-full"
                data-testid="client-save-btn"
              >
                {editingClient ? 'Enregistrer' : 'Ajouter le client'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder="Rechercher un client..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-12 bg-muted/50 border-transparent focus:border-primary"
            data-testid="client-search-input"
          />
        </div>

        {/* Client List */}
        {filteredClients.length > 0 ? (
          <div className="space-y-3">
            {filteredClients.map((client) => (
              <Card key={client.id} className="bg-card border-white/10" data-testid={`client-card-${client.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <User className="w-4 h-4 text-primary" />
                        <h3 className="font-semibold truncate">{client.name}</h3>
                      </div>
                      {client.company && (
                        <p className="text-sm text-muted-foreground mb-2">{client.company}</p>
                      )}
                      {client.client_type === 'professionnel' && client.siren && (
                        <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                          <Building className="w-3 h-3" />
                          SIREN: {client.siren}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {client.city}
                        </span>
                        {client.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {client.phone}
                          </span>
                        )}
                        {client.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {client.email}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate(canWrite() ? `/quotes/new?client=${client.id}` : '/subscription')}
                        className="h-10 w-10 text-primary hover:bg-primary/20"
                        data-testid={`client-quote-btn-${client.id}`}
                      >
                        <FileText className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(client)}
                        className="h-10 w-10 hover:bg-muted"
                        data-testid={`client-edit-btn-${client.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-10 w-10 text-destructive hover:bg-destructive/20"
                            data-testid={`client-delete-btn-${client.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-card border-white/10">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Supprimer ce client ?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Cette action est irréversible. Tous les devis associés seront conservés.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="border-white/10">Annuler</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(client.id)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Supprimer
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="bg-card border-white/10">
            <CardContent className="empty-state">
              <User className="w-12 h-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                {searchTerm ? 'Aucun résultat' : 'Aucun client'}
              </h3>
              <p className="text-muted-foreground mb-4">
                {searchTerm ? 'Essayez avec d\'autres termes' : 'Ajoutez votre premier client'}
              </p>
              {!searchTerm && (
                <Button
                  onClick={handleOpenDialog}
                  className="bg-primary text-primary-foreground hover:bg-accent rounded-full"
                  data-testid="add-first-client-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter un client
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ClientsPage;
